import { gql } from '@apollo/client';
import { useLayoutEffect } from 'react';
import * as MENUS from '../constants/menus';
import { BlogInfoFragment } from '../fragments/GeneralSettings';
import { getTranslationUrlsFromNode } from '../lib/polylangTranslations';
import { useLanguageSwitcher } from '../contexts/LanguageSwitcherContext';
import {
  Main,
  Container,
  ContentWrapper,
  EntryHeader,
  NavigationMenu,
  FeaturedImage,
  SEO,
} from '../components';
import {
  Header,
  Footer,
  HeroSection,
  ServicesSection,
  ValuePropsSection,
  FinalCTASection,
  WHY_IDENTIO_ITEMS,
} from '../components/sections';
import {
  ServicesAIDifferentiationSection,
  ServicesProcessSection,
  ServicesCaseStudiesSection,
} from '../components/sections/services';
import { getCanonicalUrl } from '../lib/siteUrl';

export default function Component(props) {
  const { setLanguageSwitcherUrls } = useLanguageSwitcher();

  if (props.loading) {
    return <>Loading...</>;
  }

  const { title: siteTitle, description: siteDescription } =
    props?.data?.generalSettings;
  const page = props?.data?.page ?? {};
  const { title, content, featuredImage, slug, uri } = page;
  const canonicalUrl = uri ? getCanonicalUrl(uri) : undefined;

  useLayoutEffect(() => {
    const urls = getTranslationUrlsFromNode(page);
    if (urls) {
      setLanguageSwitcherUrls(urls);
    }
    return () => {
      setLanguageSwitcherUrls({});
    };
  }, [page?.databaseId, page?.language?.code, setLanguageSwitcherUrls]);

  const isServicesPage = slug === 'services';

  return (
    <>
      <SEO
        title={siteTitle}
        description={siteDescription}
        imageUrl={featuredImage?.node?.sourceUrl}
        url={canonicalUrl}
      />
      <Header />
      <Main>
        {isServicesPage ? (
          <>
            <HeroSection variant="services" />
            <ServicesSection variant="services" />
            <ServicesAIDifferentiationSection />
            <ServicesProcessSection />
            <ServicesCaseStudiesSection />
            <ValuePropsSection
              title="Why Leading Organizations Choose Identio"
              subtitle="What we deliver and how we work."
              items={WHY_IDENTIO_ITEMS}
              columns={2}
            />
            <FinalCTASection variant="withForm" />
          </>
        ) : (
          <>
            <EntryHeader title={title} image={featuredImage?.node} />
            <Container>
              <ContentWrapper content={content} />
            </Container>
          </>
        )}
      </Main>
      <Footer />
    </>
  );
}

Component.variables = ({ databaseId }, ctx) => {
  return {
    databaseId,
    headerLocation: MENUS.PRIMARY_LOCATION,
    footerLocation: MENUS.FOOTER_LOCATION,
    asPreview: ctx?.asPreview,
  };
};

Component.query = gql`
  ${BlogInfoFragment}
  ${NavigationMenu.fragments.entry}
  ${FeaturedImage.fragments.entry}
  query GetPageData(
    $databaseId: ID!
    $headerLocation: MenuLocationEnum
    $footerLocation: MenuLocationEnum
    $asPreview: Boolean = false
  ) {
    page(id: $databaseId, idType: DATABASE_ID, asPreview: $asPreview) {
      title
      slug
      uri
      content
      ...FeaturedImageFragment
      language {
        code
      }
      translations {
        language {
          code
        }
        uri
        slug
      }
    }
    generalSettings {
      ...BlogInfoFragment
    }
    footerMenuItems: menuItems(where: { location: $footerLocation }) {
      nodes {
        ...NavigationMenuItemFragment
      }
    }
    headerMenuItems: menuItems(where: { location: $headerLocation }) {
      nodes {
        ...NavigationMenuItemFragment
      }
    }
  }
`;
